'use client';

import {
  captureSensorsFirstVisitFacts,
  captureSensorsMirrorFacts,
  createSensorsMirrorEventToken,
  generateUuidV4,
  type BrowserEventContext,
  type CapturedEventInput,
  type PreparedSensorsMirror,
  type SensorsFirstVisitFacts,
  type SensorsMirrorFacts,
  type SnowplowClient,
} from '@nextmar/snowplow-sdk';
import { createAnalyticsDeliveryQueue, type ChannelResult } from './deliveryQueue';
import type { SensorsEnvelope } from './manualAnalyticsTypes';

/** 自动旁路只接受神策现有自动事件，业务事件仍归原手动拥有者。 */
const AUTOMATIC_EVENTS = new Set(['$pageview', '$WebStay', '$WebPageLeave']);

/** 同一原对象只捕获一次；不同真实对象即使关联 ID 相同也各自交付。 */
interface AutomaticRecord {
  facts: SensorsMirrorFacts;
  context: BrowserEventContext;
  firstVisitFacts?: SensorsFirstVisitFacts;
  prepared?: PreparedSensorsMirror;
}

/** 同一次同步 autoTrack 的代际，不按时间窗口或历史画像配对。 */
interface AutoTrackScope {
  pages: AutomaticRecord[];
  firstVisitFacts?: SensorsFirstVisitFacts;
}

/** 文档桥接拥有配置及销毁；本模块不接管 Sensors 的发送或计时器。 */
export interface AutomaticSensorsMirror {
  observe(envelope: SensorsEnvelope): void;
  runAutoTrack<T>(execute: () => T): T;
  configure(client: SnowplowClient, enabled: boolean): void;
  flushNow(): void;
  wake(): void;
  dispose(): void;
}

/** 自动旁路仅依赖原文档的上下文捕获、共享就绪和脱敏诊断。 */
interface AutomaticMirrorDependencies {
  /** @param payload 已校验的同条最终神策包。@returns 只以该条身份事实构建的自动上下文。 */
  captureContext(payload: Readonly<Record<string, unknown>>): BrowserEventContext;
  ensureReady(): Promise<boolean>;
  diagnose(reason: string): void;
}

/**
 * 创建与当前 Sensors 初始化同寿命的自动事件旁路。
 * @param deps 原文档上下文、已有初始化 single-flight 和稳定诊断。
 * @returns 自动观察、同步作用域、配置和释放接口；绝不取消原 Sensors envelope。
 */
export function createAutomaticSensorsMirror(
  deps: AutomaticMirrorDependencies,
): AutomaticSensorsMirror {
  let client: SnowplowClient | undefined;
  let enabled = true;
  let disposed = false;
  let sequence = 0;
  let readiness: Promise<void> | undefined;
  let scope: AutoTrackScope | undefined;
  let observed = new WeakSet<object>();
  /** @param reason 稳定错误类别。@returns 无返回值；诊断异常不改变原 Sensors 调用结果。 */
  function diagnose(reason: string): void {
    try {
      deps.diagnose(reason);
    } catch {
      // 诊断观察者没有业务控制权。
    }
  }
  const queue = createAnalyticsDeliveryQueue({
    onDiagnostic: (code) => diagnose(code),
  });

  /**
   * 唤醒既有初始化，不让 pending Promise 暂停任何 Sensors 发送。
   * @returns 无返回值；共享一次就绪尝试，失败由有界队列后续重试。
   */
  function requestReady(): void {
    if (readiness || disposed) return;
    readiness = Promise.resolve()
      .then(() => deps.ensureReady())
      .then((ready) => {
        if (ready && !disposed) queue.wake();
      })
      .catch(() => diagnose('initialization_failed'))
      .finally(() => {
        readiness = undefined;
      });
  }

  /**
   * 从最终事实构建自动输入，保留普通字段并让 SDK 专门处理预置属性。
   * @param facts 已冻结的同条 Sensors 发送事实。
   * @returns PV 使用原生协议，停留与离开使用品牌行为协议。
   */
  function inputFor(facts: SensorsMirrorFacts): CapturedEventInput {
    const properties = Object.fromEntries(
      Object.entries(facts.properties).filter(
        ([key]) =>
          !key.startsWith('$') &&
          key !== 'distinct_id' &&
          key !== 'anonymous_id' &&
          key !== 'sensors_event_id',
      ),
    );
    return facts.event === '$pageview'
      ? { kind: 'pageView', properties }
      : { kind: 'track', eventName: facts.event, properties };
  }

  /**
   * 只交付观鹿镜像；失败和 pending 均不影响已经继续的原 Sensors 发送。
   * @param record 捕获时冻结的事件、页面上下文和可选同次首次画像。
   * @returns 原有有界队列的渠道结果，只有 pending 可再次纯准备。
   */
  async function deliver(record: AutomaticRecord): Promise<ChannelResult> {
    if (disposed || !enabled) return 'rejected';
    try {
      if (!client || client.getState().status !== 'ready') {
        requestReady();
        return 'pending';
      }
      if (!record.prepared) {
        const result = await client.prepareSensorsMirror(
          record.facts,
          inputFor(record.facts),
          record.context,
          record.firstVisitFacts ? { firstVisitFacts: record.firstVisitFacts } : undefined,
        );
        if (disposed) return 'rejected';
        if (result.prepared === false) {
          if (result.reason === 'not_ready') return 'pending';
          diagnose(result.reason);
          return 'rejected';
        }
        record.prepared = result.event;
      }
      const result = await client.submitPreparedSensorsMirror(record.prepared);
      if (result.status !== 'handed-off' && result.status !== 'pending') diagnose(result.reason);
      return result.status;
    } catch {
      diagnose('mirror_integration_failed');
      return 'uncertain';
    }
  }

  /**
   * 登记独立观鹿渠道，TTL 使用当前捕获时刻而非旧离开事件的发生时间。
   * @param record 已冻结记录，首次 PV 在同步 autoTrack 返回后才登记。
   * @returns 无返回值；容量拒绝仅记录诊断，不创建第二次 Sensors 调用。
   */
  function enqueue(record: AutomaticRecord): void {
    if (disposed || !enabled) return;
    const id = `automatic-${++sequence}`;
    if (
      !queue.enqueue({
        id,
        capturedAt: Date.now(),
        channels: ['snowplow'],
        deliver: () => deliver(record),
        onRemove(reason) {
          if (reason !== 'completed') diagnose(reason);
        },
      })
    )
      diagnose('queue_rejected');
    // 已在离页调用栈内的自动事件立刻进入 SDK；准备 Promise 只观察结果。
    if (record.facts.event === '$WebPageLeave' || document.visibilityState === 'hidden')
      queue.deliverNow(id);
  }

  return {
    /**
     * 同步复制发送阶段的事实，不冻结或取消官方原对象。
     * @param envelope 未被手动拥有者接管的最终原 envelope。
     * @returns 无返回值；画像仅限当前同步 autoTrack 作用域观察。
     */
    observe(envelope) {
      if (disposed || !enabled) return;
      const data: unknown = envelope.data;
      const original = envelope.origin_data ?? data;
      if (
        !data ||
        typeof data !== 'object' ||
        !original ||
        typeof original !== 'object' ||
        observed.has(original)
      )
        return;
      const payload = data as Record<string, unknown>;
      if (payload.type === 'profile_set_once') {
        observed.add(original);
        if (scope && !scope.firstVisitFacts)
          scope.firstVisitFacts = captureSensorsFirstVisitFacts(data) ?? undefined;
        return;
      }
      if (
        payload.type !== 'track' ||
        typeof payload.event !== 'string' ||
        !AUTOMATIC_EVENTS.has(payload.event)
      )
        return;
      observed.add(original);
      try {
        const properties = payload.properties;
        if (!properties || typeof properties !== 'object' || Array.isArray(properties)) {
          diagnose('invalid_final_payload');
          return;
        }
        const values = properties as Record<string, unknown>;
        if (typeof values.sensors_event_id !== 'string' || !values.sensors_event_id.trim())
          values.sensors_event_id = generateUuidV4();
        const captured = captureSensorsMirrorFacts(createSensorsMirrorEventToken(), data);
        if (captured.captured === false) {
          diagnose(captured.reason);
          return;
        }
        const record: AutomaticRecord = {
          facts: captured.facts,
          context: deps.captureContext(payload),
        };
        if (scope && captured.facts.event === '$pageview') scope.pages.push(record);
        else enqueue(record);
      } catch {
        diagnose('capture_failed');
      }
    },
    /**
     * 以调用栈绑定真实先 PV 后画像，不让后续业务画像进入先前代际。
     * @param execute 原 sensors.quick(autoTrack) 同步调用，原 this/返回值由调用方保留。
     * @returns 原调用返回值；即使抛错也清理作用域并交付已产生的 PV。
     */
    runAutoTrack(execute) {
      const previous = scope;
      const current: AutoTrackScope = { pages: [] };
      scope = current;
      try {
        return execute();
      } finally {
        scope = previous;
        for (const page of current.pages) {
          // 一次调用存在多个候选时不猜画像归属；SDK 还会约束同条$is_first_time和身份。
          if (current.pages.length === 1) page.firstVisitFacts = current.firstVisitFacts;
          enqueue(page);
        }
      }
    },
    /** @param value 同一文档唯一 Client。@param mirrorEnabled 既有解析后的渠道开关。@returns 无返回值。 */
    configure(value, mirrorEnabled) {
      if (disposed) return;
      client = value;
      enabled = mirrorEnabled;
      if (!enabled) queue.dispose();
      else queue.wake();
    },
    /** 无入参；沿用外部已有生命周期回调在当前调用栈启动待交付，返回 void。 */
    flushNow() {
      if (!disposed) queue.flushNow();
    },
    /** 无入参；恢复时只唤醒未终态记录，不制造新 PV，返回 void。 */
    wake() {
      if (!disposed) queue.wake();
    },
    /** 无入参；释放队列和弱关联，官方持有的旧 hook 之后只会旁路，返回 void。 */
    dispose() {
      disposed = true;
      queue.dispose();
      observed = new WeakSet();
      scope = undefined;
      client = undefined;
    },
  };
}
